import React from "react";
import {
  Box,
  Grid,
  GridItem,
  Text,
  Badge,
  useColorModeValue,
  Icon,
  HStack,
  VStack,
  Divider,
  Tooltip,
} from "@chakra-ui/react";
import { 
  TimeIcon, 
  WarningIcon, 
  CheckCircleIcon, 
  InfoIcon,
  CalendarIcon 
} from "@chakra-ui/icons";

export default function DetailKenaikanPegawai({ kepegawaian }) {
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textPrimary = useColorModeValue("gray.800", "white");
  const textSecondary = useColorModeValue("gray.600", "gray.400");

  // Fungsi untuk menghitung kenaikan pangkat
  const hitungKenaikanPangkat = () => {
    if (!kepegawaian.tmt_pangkat_terakhir) {
      return {
        status: 'no_data',
        message: 'TMT Pangkat belum diisi',
        color: 'gray',
        icon: InfoIcon
      };
    }

    const today = new Date();
    const tmtPangkat = new Date(kepegawaian.tmt_pangkat_terakhir);
    
    // Hitung penundaan hukdis
    const penundaanBulan = (kepegawaian.hukdis && kepegawaian.lama_hukdis) 
      ? parseInt(kepegawaian.lama_hukdis) 
      : 0;
    
    // Hitung umur pangkat dalam bulan
    const diffTime = today - tmtPangkat;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    const umurBulan = Math.floor(diffDays / 30);
    const umurTahun = Math.floor(umurBulan / 12);
    const sisaBulan = umurBulan % 12;
    
    // Threshold: 3 tahun 10 bulan + penundaan hukdis
    const thresholdBulan = (3 * 12) + 10 + penundaanBulan;
    
    // Deadline: 4 tahun + penundaan hukdis
    const deadlineDate = new Date(tmtPangkat);
    deadlineDate.setFullYear(deadlineDate.getFullYear() + 4);
    deadlineDate.setMonth(deadlineDate.getMonth() + penundaanBulan);
    
    const deadlineStr = deadlineDate.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    
    // Hitung sisa waktu ke deadline
    const sisaWaktu = deadlineDate - today;
    const sisaHari = Math.floor(sisaWaktu / (1000 * 60 * 60 * 24));
    const sisaTahun = Math.floor(sisaHari / 365);
    const sisaBulanHitung = Math.floor((sisaHari % 365) / 30);
    const sisaHariHitung = sisaHari % 30;
    
    let status, message, color, icon, detail;
    
    if (umurBulan < thresholdBulan) {
      // Belum saatnya
      const kurangBulan = thresholdBulan - umurBulan;
      const kurangTahun = Math.floor(kurangBulan / 12);
      const kurangSisaBulan = kurangBulan % 12;
      
      status = 'belum_saatnya';
      message = 'Belum Saatnya Naik Pangkat';
      color = 'blue';
      icon = TimeIcon;
      detail = `Masih ${kurangTahun} tahun ${kurangSisaBulan} bulan lagi`;
    } else if (deadlineDate > today) {
      // Sudah bisa diajukan, belum lewat deadline
      status = 'segera';
      message = 'Segera Ajukan Kenaikan Pangkat!';
      color = 'yellow';
      icon = WarningIcon;
      detail = `Deadline: ${sisaTahun} tahun ${sisaBulanHitung} bulan ${sisaHariHitung} hari lagi`;
    } else {
      // Sudah lewat deadline
      const lewatHari = Math.abs(sisaHari);
      const lewatTahun = Math.floor(lewatHari / 365);
      const lewatBulan = Math.floor((lewatHari % 365) / 30);
      const lewatSisaHari = lewatHari % 30;
      
      status = 'terlambat';
      message = 'TERLAMBAT! Segera Proses!';
      color = 'red';
      icon = WarningIcon;
      detail = `Terlambat ${lewatTahun} tahun ${lewatBulan} bulan ${lewatSisaHari} hari`;
    }
    
    return {
      status,
      message,
      color,
      icon,
      detail,
      deadlineStr,
      umurJabatan: `${umurTahun} tahun ${sisaBulan} bulan`,
      penundaanBulan,
      adaHukdis: penundaanBulan > 0
    };
  };

  // Fungsi untuk menghitung kenaikan gaji berkala
  const hitungKenaikanGaji = () => {
    if (!kepegawaian.tmt_gaji_terakhir) {
      return {
        status: 'no_data',
        message: 'TMT Gaji belum diisi',
        color: 'gray',
        icon: InfoIcon
      };
    }

    const today = new Date();
    const tmtGaji = new Date(kepegawaian.tmt_gaji_terakhir);
    
    // Hitung umur gaji dalam bulan
    const diffTime = today - tmtGaji;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    const umurBulan = Math.floor(diffDays / 30);
    const umurTahun = Math.floor(umurBulan / 12);
    const sisaBulan = umurBulan % 12;
    
    // Threshold: 1 tahun 10 bulan
    const thresholdBulan = (1 * 12) + 10;
    
    // Deadline: 2 tahun
    const deadlineDate = new Date(tmtGaji);
    deadlineDate.setFullYear(deadlineDate.getFullYear() + 2);
    
    const deadlineStr = deadlineDate.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    
    // Hitung sisa waktu ke deadline
    const sisaWaktu = deadlineDate - today;
    const sisaHari = Math.floor(sisaWaktu / (1000 * 60 * 60 * 24));
    const sisaTahun = Math.floor(sisaHari / 365);
    const sisaBulanHitung = Math.floor((sisaHari % 365) / 30);
    const sisaHariHitung = sisaHari % 30;
    
    let status, message, color, icon, detail;
    
    if (umurBulan < thresholdBulan) {
      // Belum saatnya
      const kurangBulan = thresholdBulan - umurBulan;
      const kurangTahun = Math.floor(kurangBulan / 12);
      const kurangSisaBulan = kurangBulan % 12;
      
      status = 'belum_saatnya';
      message = 'Belum Saatnya KGB';
      color = 'blue';
      icon = TimeIcon;
      detail = `Masih ${kurangTahun} tahun ${kurangSisaBulan} bulan lagi`;
    } else if (deadlineDate > today) {
      // Sudah bisa diajukan, belum lewat deadline
      status = 'segera';
      message = 'Segera Ajukan KGB!';
      color = 'yellow';
      icon = WarningIcon;
      detail = `Deadline: ${sisaTahun} tahun ${sisaBulanHitung} bulan ${sisaHariHitung} hari lagi`;
    } else {
      // Sudah lewat deadline
      const lewatHari = Math.abs(sisaHari);
      const lewatTahun = Math.floor(lewatHari / 365);
      const lewatBulan = Math.floor((lewatHari % 365) / 30);
      const lewatSisaHari = lewatHari % 30;
      
      status = 'terlambat';
      message = 'TERLAMBAT! Segera Proses!';
      color = 'red';
      icon = WarningIcon;
      detail = `Terlambat ${lewatTahun} tahun ${lewatBulan} bulan ${lewatSisaHari} hari`;
    }
    
    return {
      status,
      message,
      color,
      icon,
      detail,
      deadlineStr,
      umurGaji: `${umurTahun} tahun ${sisaBulan} bulan`
    };
  };

  const infoPangkat = hitungKenaikanPangkat();
  const infoGaji = hitungKenaikanGaji();

  return (
    <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
      {/* Card Kenaikan Pangkat */}
      <GridItem>
        <Box
          bg={bgCard}
          border="2px solid"
          borderColor={infoPangkat.status === 'terlambat' ? 'red.400' : 
                      infoPangkat.status === 'segera' ? 'orange.400' : 
                      infoPangkat.status === 'belum_saatnya' ? 'blue.400' : borderColor}
          borderRadius="lg"
          p={5}
          shadow="md"
        >
          <HStack justify="space-between" mb={4}>
            <HStack>
              <Icon as={CalendarIcon} boxSize={5} color="orange.500" />
              <Text fontSize="lg" fontWeight="bold" color={textPrimary}>
                Kenaikan Pangkat
              </Text>
            </HStack>
            <Badge colorScheme={infoPangkat.color} fontSize="xs" px={2} py={1}>
              <Icon as={infoPangkat.icon} mr={1} />
              {infoPangkat.status === 'no_data' ? 'NO DATA' :
               infoPangkat.status === 'terlambat' ? 'TERLAMBAT' :
               infoPangkat.status === 'segera' ? 'SEGERA' : 'NORMAL'}
            </Badge>
          </HStack>

          <Divider mb={4} />

          <VStack align="stretch" spacing={3}>
            {infoPangkat.status === 'no_data' ? (
              <Box textAlign="center" py={4}>
                <Icon as={InfoIcon} boxSize={10} color="gray.400" mb={2} />
                <Text color={textSecondary} fontSize="sm">
                  {infoPangkat.message}
                </Text>
                <Text color={textSecondary} fontSize="xs" mt={1}>
                  Silakan isi TMT Pangkat Terakhir terlebih dahulu
                </Text>
              </Box>
            ) : (
              <>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={textSecondary}>TMT Pangkat:</Text>
                  <Text fontSize="sm" fontWeight="medium" color={textPrimary}>
                    {new Date(kepegawaian.tmt_pangkat_terakhir).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color={textSecondary}>Umur Pangkat:</Text>
                  <Badge colorScheme="blue" fontSize="xs">
                    {infoPangkat.umurJabatan}
                  </Badge>
                </HStack>

                {infoPangkat.adaHukdis && (
                  <HStack justify="space-between">
                    <Text fontSize="sm" color={textSecondary}>Penundaan Hukdis:</Text>
                    <Tooltip label={kepegawaian.hukdis} placement="top" hasArrow>
                      <Badge colorScheme="red" fontSize="xs" cursor="help">
                        <Icon as={InfoIcon} mr={1} />
                        +{infoPangkat.penundaanBulan} bulan
                      </Badge>
                    </Tooltip>
                  </HStack>
                )}

                <HStack justify="space-between">
                  <Text fontSize="sm" color={textSecondary}>Deadline (4 tahun):</Text>
                  <Text fontSize="sm" fontWeight="bold" color="orange.600">
                    {infoPangkat.deadlineStr}
                  </Text>
                </HStack>

                <Divider />

                <Box 
                  bg={infoPangkat.status === 'terlambat' ? 'red.50' : 
                      infoPangkat.status === 'segera' ? 'orange.50' : 'blue.50'}
                  p={3}
                  borderRadius="md"
                  textAlign="center"
                >
                  <Text 
                    fontSize="md" 
                    fontWeight="bold" 
                    color={infoPangkat.status === 'terlambat' ? 'red.700' : 
                           infoPangkat.status === 'segera' ? 'orange.700' : 'blue.700'}
                  >
                    {infoPangkat.message}
                  </Text>
                  <Text 
                    fontSize="sm" 
                    color={infoPangkat.status === 'terlambat' ? 'red.600' : 
                           infoPangkat.status === 'segera' ? 'orange.600' : 'blue.600'}
                    mt={1}
                  >
                    {infoPangkat.detail}
                  </Text>
                </Box>
              </>
            )}
          </VStack>
        </Box>
      </GridItem>

      {/* Card Kenaikan Gaji Berkala */}
      <GridItem>
        <Box
          bg={bgCard}
          border="2px solid"
          borderColor={infoGaji.status === 'terlambat' ? 'red.400' : 
                      infoGaji.status === 'segera' ? 'green.400' : 
                      infoGaji.status === 'belum_saatnya' ? 'blue.400' : borderColor}
          borderRadius="lg"
          p={5}
          shadow="md"
        >
          <HStack justify="space-between" mb={4}>
            <HStack>
              <Icon as={CalendarIcon} boxSize={5} color="green.500" />
              <Text fontSize="lg" fontWeight="bold" color={textPrimary}>
                Kenaikan Gaji Berkala
              </Text>
            </HStack>
            <Badge colorScheme={infoGaji.color} fontSize="xs" px={2} py={1}>
              <Icon as={infoGaji.icon} mr={1} />
              {infoGaji.status === 'no_data' ? 'NO DATA' :
               infoGaji.status === 'terlambat' ? 'TERLAMBAT' :
               infoGaji.status === 'segera' ? 'SEGERA' : 'NORMAL'}
            </Badge>
          </HStack>

          <Divider mb={4} />

          <VStack align="stretch" spacing={3}>
            {infoGaji.status === 'no_data' ? (
              <Box textAlign="center" py={4}>
                <Icon as={InfoIcon} boxSize={10} color="gray.400" mb={2} />
                <Text color={textSecondary} fontSize="sm">
                  {infoGaji.message}
                </Text>
                <Text color={textSecondary} fontSize="xs" mt={1}>
                  Silakan isi TMT Gaji Terakhir terlebih dahulu
                </Text>
              </Box>
            ) : (
              <>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={textSecondary}>TMT Gaji:</Text>
                  <Text fontSize="sm" fontWeight="medium" color={textPrimary}>
                    {new Date(kepegawaian.tmt_gaji_terakhir).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color={textSecondary}>Umur Gaji:</Text>
                  <Badge colorScheme="blue" fontSize="xs">
                    {infoGaji.umurGaji}
                  </Badge>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color={textSecondary}>Deadline (2 tahun):</Text>
                  <Text fontSize="sm" fontWeight="bold" color="green.600">
                    {infoGaji.deadlineStr}
                  </Text>
                </HStack>

                <Divider />

                <Box 
                  bg={infoGaji.status === 'terlambat' ? 'red.50' : 
                      infoGaji.status === 'segera' ? 'green.50' : 'blue.50'}
                  p={3}
                  borderRadius="md"
                  textAlign="center"
                >
                  <Text 
                    fontSize="md" 
                    fontWeight="bold" 
                    color={infoGaji.status === 'terlambat' ? 'red.700' : 
                           infoGaji.status === 'segera' ? 'green.700' : 'blue.700'}
                  >
                    {infoGaji.message}
                  </Text>
                  <Text 
                    fontSize="sm" 
                    color={infoGaji.status === 'terlambat' ? 'red.600' : 
                           infoGaji.status === 'segera' ? 'green.600' : 'blue.600'}
                    mt={1}
                  >
                    {infoGaji.detail}
                  </Text>
                </Box>
              </>
            )}
          </VStack>
        </Box>
      </GridItem>
    </Grid>
  );
}